import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DynamicTitle from "./DynamicTitle";
import WaveDivider from "./WaveDivider";
import { trackEvent } from "../utils/posthog";
import { trackInitiateCheckout } from "../utils/metaPixel";

/**
 * Landing Page Version A: Pain-Point / Emotional Focus
 * Target Audiences: The Waitlisted, Treatment-Resistant, Housebound
 * 
 * Key themes:
 * - Empathy and understanding
 * - Accessibility when nothing else is available
 * - Privacy and safety
 * - Limited-time offer with real value
 */

// Stripe Payment Links - Direct to checkout
const STRIPE_LINKS = {
  monthly: "https://buy.stripe.com/5kQbJ18MweJIbT90GteME04",
  annual: "https://buy.stripe.com/fZu6oH2o89poe1hbl7eME06",
};

function LandingPageA() {
  // Calculate time remaining for offer (creates urgency)
  const [timeRemaining, setTimeRemaining] = useState("");
  
  // Track landing page view on mount
  useEffect(() => {
    trackEvent('landing_page_viewed', {
      variant: 'A',
      variant_name: 'pain_point_emotional',
      page_url: window.location.href,
    });
  }, []);

  // Helper to track checkout clicks
  const handleCheckoutClick = (plan) => {
    trackEvent('checkout_initiated', {
      variant: 'A',
      plan_type: plan,
      source: 'landing_page',
    });
    trackInitiateCheckout({ content_type: 'product', content_ids: [plan] });
  };
  
  useEffect(() => {
    // Set offer end to end of current week (Sunday midnight)
    const now = new Date();
    const endOfWeek = new Date(now);
    endOfWeek.setDate(now.getDate() + (7 - now.getDay()));
    endOfWeek.setHours(23, 59, 59, 999);
    
    const updateTimer = () => {
      const currentTime = new Date();
      const diff = endOfWeek - currentTime;
      
      if (diff <= 0) {
        setTimeRemaining("Offer Ending Soon");
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h remaining`);
      } else {
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining(`${hours}h ${minutes}m remaining`);
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <DynamicTitle>Find Relief — Special Offer</DynamicTitle>
      
      {/* Minimal Header - Landing Page Best Practice */}
      <header className="bg-background py-4 px-4 border-b border-tint">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="font-bold text-2xl text-primary">
            RealignMind
          </Link>
          <span className="text-sm text-textSecondary hidden sm:block">
            Self-Directed Wellness Tool
          </span>
        </div>
      </header>

      {/* Urgency Banner */}
      <div className="bg-secondary text-white py-2 px-4 text-center text-sm">
        <span className="font-semibold">Limited Time Offer</span>
        <span className="mx-2">•</span>
        <span>Use code <span className="bg-white/20 px-2 py-0.5 rounded font-mono font-bold">LAUNCH15</span> for 15% off</span>
        <span className="mx-2">•</span>
        <span className="font-medium">{timeRemaining}</span>
      </div>

      <main className="flex-grow">
        {/* Hero Section - Emotional/Pain-Point Focus */}
        <section className="bg-surfaceTint py-12 md:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            {/* Social Proof Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-2 rounded-full mb-6 shadow-sm">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">R</div>
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold">M</div>
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-white text-xs font-bold">K</div>
              </div>
              <span className="text-sm text-textSecondary">Join thousands finding calm</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text mb-6 leading-tight">
              Months on a Waitlist?<br />
              <span className="text-accent">Start Finding Relief Today</span>
            </h1>
            
            <p className="text-lg md:text-xl text-textSecondary mb-4 max-w-2xl mx-auto">
              When professional help feels out of reach, you don't have to wait in silence. 
              Access research-backed bilateral stimulation techniques from the comfort of your home.
            </p>
            
            <p className="text-base text-textSecondary mb-8 max-w-xl mx-auto">
              A self-directed wellness tool for when you need support now—not months from now.
            </p>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <a
                href={STRIPE_LINKS.annual}
                onClick={() => handleCheckoutClick('annual')}
                className="w-full sm:w-auto inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
              >
                Start Your Free 30-Day Trial
              </a>
            </div>
            
            {/* Trust Signals Under CTA */}
            <div className="flex flex-wrap justify-center gap-4 text-sm text-textSecondary">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Cancel anytime during trial
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Cancel anytime
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                100% Private
              </span>
            </div>
          </div>
        </section>

        <WaveDivider topColor="transparent" bottomColor="background" />

        {/* What is Bilateral Stimulation */}
        <section className="py-12 md:py-16 bg-surfaceTint">
          <div className="max-w-4xl mx-auto px-4">
            <div className="md:flex md:items-center md:gap-12">
              <div className="md:w-1/2 mb-8 md:mb-0">
                <div className="rounded-2xl overflow-hidden border border-tint">
                  <img 
                    src="/images/Dot.gif" 
                    alt="Bilateral stimulation in action - a dot moving smoothly left to right" 
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-center text-sm text-textSecondary mt-3">
                  Follow the dot with your eyes as it moves side to side
                </p>
              </div>
              <div className="md:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">
                  What is Bilateral Stimulation?
                </h2>
                <p className="text-textSecondary mb-4">
                  Bilateral stimulation uses gentle, rhythmic left-right sensory input—like following 
                  a moving dot with your eyes or hearing alternating tones.
                </p>
                <p className="text-textSecondary mb-4">
                  Research shows this technique can help promote relaxation, reduce stress, 
                  and support emotional processing.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-textSecondary">Non-invasive and gentle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-textSecondary">Research-backed techniques</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-textSecondary">You're always in control</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* Understanding Section - Empathy First */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">
                We Understand What You're Going Through
              </h2>
              <p className="text-textSecondary max-w-2xl mx-auto">
                You're not alone in feeling stuck. Many people face these same barriers to getting help.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl border border-tint">
                <p className="text-textSecondary italic mb-4">
                  "I've been on a waitlist for 4 months. I just need something I can do NOW."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">W</div>
                  <div>
                    <p className="font-medium text-text">Waiting for Care</p>
                    <p className="text-sm text-textSecondary">We fill the gap while you wait</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl border border-tint">
                <p className="text-textSecondary italic mb-4">
                  "I can't afford $150+ per session. I need something that fits my budget."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">C</div>
                  <div>
                    <p className="font-medium text-text">Cost Concerns</p>
                    <p className="text-sm text-textSecondary">Less than $1/day, unlimited access</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl border border-tint">
                <p className="text-textSecondary italic mb-4">
                  "Some days I can't leave my house. I need help that comes to me."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">H</div>
                  <div>
                    <p className="font-medium text-text">Homebound</p>
                    <p className="text-sm text-textSecondary">Access from anywhere, anytime</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl border border-tint">
                <p className="text-textSecondary italic mb-4">
                  "I'm not ready to talk to someone. I need something I can do on my own."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">P</div>
                  <div>
                    <p className="font-medium text-text">Privacy First</p>
                    <p className="text-sm text-textSecondary">Self-directed, no judgment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Special Offer Section */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-surfaceTint rounded-3xl p-8 md:p-12 border border-tint">
              <div className="text-center mb-8">
                <div className="inline-block bg-accent/10 text-accent font-semibold px-4 py-1 rounded-full text-sm mb-4">
                  Limited Time Offer
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-text mb-2">
                  Get Started with Our Best Deal
                </h2>
                <p className="text-textSecondary">
                  Save $48 when you choose annual billing
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Monthly Option */}
                <div className="bg-white rounded-2xl p-6 border border-tint">
                  <h3 className="font-semibold text-text mb-2">Monthly</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-text">$19</span>
                    <span className="text-textSecondary">/month</span>
                  </div>
                  <ul className="space-y-2 text-sm text-textSecondary mb-6">
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      7-day free trial
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      Unlimited sessions
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      Cancel anytime
                    </li>
                  </ul>
                  <a
                    href={STRIPE_LINKS.monthly}
                    onClick={() => handleCheckoutClick('monthly')}
                    className="block w-full text-center bg-background hover:bg-surfaceTint text-text font-semibold py-3 px-6 rounded-full border border-tint transition-colors"
                  >
                    Start 7-Day Trial
                  </a>
                </div>

                {/* Annual Option - Highlighted */}
                <div className="bg-gradient-to-br from-accent to-primary rounded-2xl p-6 text-white relative overflow-hidden">
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-bold">
                    BEST VALUE
                  </div>
                  <h3 className="font-semibold mb-2">Annual</h3>
                  <div className="mb-1">
                    <span className="text-3xl font-bold">$15</span>
                    <span className="opacity-90">/month</span>
                  </div>
                  <p className="text-sm opacity-90 mb-4">
                    <span className="line-through opacity-75">$228</span> $180 billed annually
                  </p>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <strong>30-day free trial</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      Unlimited sessions
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      Save $48/year (21% off)
                    </li>
                  </ul>
                  <a
                    href={STRIPE_LINKS.annual}
                    onClick={() => handleCheckoutClick('annual')}
                    className="block w-full text-center bg-white text-accent hover:bg-surfaceTint font-bold py-3 px-6 rounded-full transition-colors"
                  >
                    Start Free Trial
                  </a>
                </div>
              </div>

              {/* Promo Code Callout */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-3 bg-secondary/10 border-2 border-dashed border-secondary/30 text-secondary px-6 py-3 rounded-xl">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span>Use code <span className="font-mono font-bold bg-secondary text-white px-2 py-1 rounded">LAUNCH15</span> at checkout for <strong>15% off</strong></span>
                </div>
              </div>

              {/* Money-Back Guarantee */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong>30-Day Money-Back Guarantee</strong> — No questions asked</span>
                </div>
                <p className="text-center text-sm text-textSecondary mt-4 px-2">
                  Money tight? If cost is a consideration at all, email{" "}
                  <a href="mailto:access@realignmind.com?subject=Financial%20Assistance%20Request" className="text-accent hover:underline font-medium">
                    access@realignmind.com
                  </a>{" "}
                  and we'll get you access.
                </p>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* Trust & Safety Section */}
        <section className="py-12 md:py-16 bg-surfaceTint">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-10">
              Your Privacy & Safety Come First
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl text-center border border-tint">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Your Data Stays Private</h3>
                <p className="text-sm text-textSecondary">
                  We never sell your data. Your sessions are your own. Period.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl text-center border border-tint">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Built-In Safety Features</h3>
                <p className="text-sm text-textSecondary">
                  Pause or stop anytime. Crisis resources always accessible.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl text-center border border-tint">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Easy Cancellation</h3>
                <p className="text-sm text-textSecondary">
                  Cancel in one click from your account. No hoops, no hassle.
                </p>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* FAQ Section - Brief */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-10">
              Questions? We've Got Answers
            </h2>

            <div className="space-y-6">
              <div className="border-b border-tint pb-6">
                <h3 className="font-semibold text-text mb-2">Is this a replacement for therapy?</h3>
                <p className="text-textSecondary">
                  No. RealignMind is a wellness tool that can support your overall well-being, 
                  but it does not diagnose, treat, cure, or prevent any medical condition. 
                  It's designed to complement—not replace—professional care.
                </p>
              </div>

              <div className="border-b border-tint pb-6">
                <h3 className="font-semibold text-text mb-2">How does the free trial work?</h3>
                <p className="text-textSecondary">
                  You get full access to all features during your trial. You won't be charged until 
                  the trial ends, and you can cancel anytime before then—no questions asked.
                </p>
              </div>

              <div className="border-b border-tint pb-6">
                <h3 className="font-semibold text-text mb-2">What if it doesn't work for me?</h3>
                <p className="text-textSecondary">
                  We offer a 30-day money-back guarantee even after your trial ends. 
                  If you're not satisfied, contact us for a full refund.
                </p>
              </div>

              <div className="pb-6">
                <h3 className="font-semibold text-text mb-2">Is my data safe?</h3>
                <p className="text-textSecondary">
                  Yes. We never sell your data to third parties. Your session data is private 
                  and protected. You can read our full{" "}
                  <Link to="/privacy-policy" className="text-accent hover:underline">privacy policy</Link> for details.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-12 md:py-16 bg-surfaceTint">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">
              You've Waited Long Enough
            </h2>
            <p className="text-lg text-textSecondary mb-8 max-w-xl mx-auto">
              Start your journey toward feeling better today. 
              30 days free — cancel anytime if it's not for you.
            </p>
            <a
              href={STRIPE_LINKS.annual}
              onClick={() => handleCheckoutClick('annual')}
              className="inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-10 rounded-full text-lg transition-colors"
            >
              Start Your Free Trial Now
            </a>
            <p className="text-sm text-textSecondary mt-4">
              Join thousands who've already taken the first step
            </p>
          </div>
        </section>
      </main>

      {/* Minimal Footer */}
      <footer className="bg-secondary text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="font-bold text-lg mb-1">RealignMind</p>
              <p className="text-sm text-white/70">
                &copy; {new Date().getFullYear()} Realign LLC. All rights reserved.
              </p>
            </div>
            <nav>
              <ul className="flex flex-wrap justify-center gap-4 text-sm">
                <li><Link to="/privacy-policy" className="text-white/80 hover:text-white">Privacy</Link></li>
                <li><Link to="/terms-of-service" className="text-white/80 hover:text-white">Terms</Link></li>
                <li><Link to="/disclaimer" className="text-white/80 hover:text-white">Disclaimer</Link></li>
                <li><Link to="/emergency-support" className="text-white/80 hover:text-white">Crisis Help</Link></li>
              </ul>
            </nav>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-xs text-white/60">
              RealignMind is a wellness tool and does not diagnose, treat, cure, or prevent any disease or medical condition. 
              This is not a substitute for professional medical advice, diagnosis, or treatment.
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-tint p-4 md:hidden z-50 shadow-sm">
        <a
          href={STRIPE_LINKS.annual}
          onClick={() => handleCheckoutClick('annual')}
          className="block w-full text-center bg-accent hover:bg-accentDark text-white font-bold py-3 px-6 rounded-full transition-colors"
        >
          Start Free Trial — Save $48
        </a>
      </div>
      
      {/* Spacer for sticky CTA on mobile */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}

export default LandingPageA;
